import React from "react";
import sealImg from "@/assets/catalog/usps-trademarks/technician-seal-of-safety.webp";
import CpAccordion from "../../components/CpAccordion/CpAccordion";
import CpButton from "../../components/CpButton/CpButton";
import CpIcon from "../../components/CpIcon/CpIcon";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import CpSectionHeading from "../../components/CpSectionHeading/CpSectionHeading";
import { USP_LICENSING_FILE_URLS, UspTrade } from "./uspsTrademarksLinks";

const TIERS = ["Build", "Grow", "Lead", "Scale"];

const GLANCE = [
  { icon: "badge-check", label: "USPs and company names for your trade" },
  { icon: "shield-check", label: "The Technician Seal of Safety" },
  { icon: "trophy", label: "Trade-specific differentiators and more" },
];

const MARKS = [
  {
    icon: "badge-check",
    title: "USPs & Company Names",
    body: "Memorable USPs and company names built for your trade. Review the marks available below.",
  },
  {
    icon: "shield-check",
    title: "Technician Seal of Safety",
    body: "A mark with specific criteria that tells homeowners your team is drug free, background-checked and fully trained.",
  },
  {
    icon: "trophy",
    title: "Trade-Specific Differentiators",
    body: "A collection of trademarks that help you stand out from the competition in your trade.",
  },
  {
    icon: "layers",
    title: "Additional Licensable Marks",
    body: "More marks available from CertainPath that you can license for your business.",
  },
];

const SEAL = [
  { icon: "circle-check", label: "Drug tested" },
  { icon: "user-search", label: "Background-checked" },
  { icon: "graduation-cap", label: "Fully professionally trained" },
];

const TRADES: { name: UspTrade; icon: string }[] = [
  { name: "HVAC", icon: "fan" },
  { name: "Electrical", icon: "zap" },
  { name: "Plumbing", icon: "droplets" },
  { name: "Roofing", icon: "house" },
];

const FAQS = [
  {
    title: "Who can license CertainPath USPs?",
    body: "USPs and trademarks are exclusive to current CertainPath members, at every membership stage: Build, Grow, Lead and Scale.",
  },
  {
    title: "How much does it cost?",
    body: "CertainPath licenses each USP to you for $1 per year.",
  },
  {
    title: "How do I get started?",
    body: "Download the licensing agreement for your trade above to see the available marks and start the licensing process.",
  },
  {
    title: "What does the Technician Seal of Safety require?",
    body: "The Seal carries specific criteria. It shows homeowners your technicians are drug free, background-checked and fully trained.",
  },
];

const pill: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 999,
  background: "var(--white)",
  border: "1px solid var(--border-default)",
  fontSize: 13,
  fontWeight: 600,
};

const rowLabel: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--grey-500)",
  marginRight: 4,
};

const fileLink: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid var(--border-default)",
  background: "var(--cream-100)",
  color: "var(--ink-500)",
  textDecoration: "none",
};

interface AgreementLinkProps {
  href: string | null;
  label: string;
  meta: string;
}

/** A trade's PDF download; renders nothing until its permanent URL exists. */
const AgreementLink: React.FC<AgreementLinkProps> = ({ href, label, meta }) => {
  if (!href) return null;
  return (
    <a href={href} rel="noopener noreferrer" style={fileLink} target="_blank">
      <CpIcon color="var(--red-500)" name="file-down" size={20} />
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{label}</span>
        <span
          style={{ fontSize: 12, fontWeight: 600, color: "var(--grey-500)" }}
        >
          {meta}
        </span>
      </span>
    </a>
  );
};

const UspsTrademarksPage: React.FC = () => (
  <CpLandingShell title="USPs & Trademarks">
    {/* Hero */}
    <section
      style={{
        background: "var(--cream-100)",
        padding: "clamp(56px, 7vw, 96px) 0",
      }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                borderRadius: 999,
                background: "var(--red-50)",
                border: "1px solid rgba(178,0,48,.15)",
                color: "var(--red-500)",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--red-500)",
                  boxShadow: "0 0 0 3px rgba(178,0,48,.18)",
                }}
              />
              Resources &amp; Programs
            </span>
          </div>
          <h1
            style={{
              margin: "22px 0 0",
              fontSize: "clamp(52px, 7.4vw, 96px)",
              lineHeight: 0.95,
              letterSpacing: "-.04em",
              fontWeight: 800,
              color: "var(--red-500)",
            }}
          >
            CertainPath USPs
          </h1>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: "clamp(22px, 2.4vw, 30px)",
              fontWeight: 300,
              letterSpacing: "-.015em",
              lineHeight: 1.2,
              color: "var(--ink-500)",
            }}
          >
            Unique Selling Propositions &amp; Trademarks
          </p>
          <p
            style={{
              margin: "22px 0 0",
              maxWidth: "36ch",
              fontSize: "clamp(18px, 1.6vw, 21px)",
              lineHeight: 1.5,
              color: "var(--ink-500)",
            }}
          >
            Use members-only trademarks to build trust and stand out locally —
            without the time and cost of developing your own.
          </p>

          <div
            style={{
              marginTop: 26,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={rowLabel}>Built for</span>
            <span style={pill}>Owners</span>
            <span style={pill}>All Trades</span>
          </div>
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={rowLabel}>Available on</span>
            {TIERS.map((t) => (
              <span
                key={t}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 16px 7px 14px",
                  borderRadius: 999,
                  background: "var(--ink-500)",
                  color: "var(--cream-100)",
                  fontSize: 13,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                <CpIcon color="var(--gold-400)" name="check" size={13} />
                {t}
              </span>
            ))}
          </div>

          <div
            style={{
              marginTop: 34,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <CpButton href="#agreements" size="lg">
              Get Your Trade&apos;s Agreements
            </CpButton>
            <CpButton href="#marks" size="lg" variant="secondary">
              See Available Marks
            </CpButton>
          </div>
        </div>

        <div
          aria-label="Licensing at a glance"
          style={{
            background: "var(--ink-500)",
            borderRadius: 24,
            boxShadow: "var(--shadow-lg)",
            overflow: "hidden",
            color: "var(--cream-100)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              borderBottom: "1px solid var(--border-inverse)",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              Licensing at a glance
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--gold-400)",
              }}
            >
              Members Only
            </span>
          </div>
          <div style={{ padding: "8px 24px" }}>
            {GLANCE.map((g) => (
              <div
                key={g.label}
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: "1px solid var(--border-inverse)",
                }}
              >
                <CpIcon color="var(--gold-400)" name={g.icon} size={20} />
                <span
                  style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}
                >
                  {g.label}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              padding: "20px 24px 24px",
              background: "var(--ink-900)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "var(--gold-400)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <CpIcon color="var(--ink-500)" name="shield-check" size={20} />
            </div>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--cream-100)",
              }}
            >
              Exclusive to current CertainPath members
            </span>
          </div>
        </div>
      </div>
    </section>

    {/* What Are USPs */}
    <section
      style={{
        background: "var(--ink-500)",
        padding: "clamp(56px, 7vw, 96px) 0",
      }}
    >
      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
          gap: "24px 64px",
          alignItems: "start",
        }}
      >
        <h2
          style={{
            margin: 0,
            color: "var(--gold-400)",
            fontSize: "clamp(30px, 3.2vw, 42px)",
            fontWeight: 800,
            letterSpacing: "-.025em",
            lineHeight: 1.12,
          }}
        >
          What are CertainPath USPs?
        </h2>
        <div>
          <p
            style={{
              margin: 0,
              color: "var(--cream-100)",
              fontSize: "clamp(20px, 1.9vw, 24px)",
              fontWeight: 300,
              lineHeight: 1.45,
            }}
          >
            A memorable USP takes years and real money to build.{" "}
            <strong style={{ fontWeight: 800 }}>
              As a member, you can license one for $1.
            </strong>
          </p>
          <p
            style={{
              margin: "18px 0 0",
              color: "var(--cream-100)",
              fontSize: 17,
              lineHeight: 1.65,
            }}
          >
            CertainPath members can license unique selling propositions, company
            names and other trademarks available only to CertainPath members.
            Put a proven mark to work in your business and give homeowners a
            clear reason to choose you.
          </p>
        </div>
      </div>
    </section>

    {/* Available Marks */}
    <section
      id="marks"
      style={{ background: "var(--sand-200)", padding: "var(--section-y) 0" }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
        }}
      >
        <CpSectionHeading
          align="center"
          eyebrow="Available Marks"
          title="Four Ways to Stand Out in Your Market"
        />
        <div
          data-stagger=""
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
            gap: 20,
            marginTop: 48,
          }}
        >
          {MARKS.map((m) => (
            <div
              key={m.title}
              style={{
                background: "var(--white)",
                border: "1px solid var(--border-default)",
                borderRadius: 16,
                padding: 32,
                display: "grid",
                gridTemplateColumns: "56px minmax(0, 1fr)",
                gap: 20,
                alignItems: "start",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: "var(--ink-500)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CpIcon color="var(--gold-400)" name={m.icon} size={26} />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 20,
                    lineHeight: 1.3,
                    letterSpacing: "-.01em",
                  }}
                >
                  {m.title}
                </h3>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: "var(--text-muted)",
                    textWrap: "pretty",
                  }}
                >
                  {m.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Technician Seal of Safety */}
    <section
      id="seal"
      style={{ background: "var(--white)", padding: "var(--section-y) 0" }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div>
          <CpSectionHeading
            eyebrow="Featured Mark"
            lead="Show homeowners the people you send into their home are drug free, background-checked and fully trained."
            title="The Technician Seal of Safety"
          />
          <p
            style={{
              margin: "20px 0 0",
              fontSize: 17,
              lineHeight: 1.65,
              maxWidth: "56ch",
            }}
          >
            The Seal carries specific criteria, which is what makes it a
            powerful differentiator in your market — and gives homeowners peace
            of mind when choosing your company.
          </p>
          <div
            style={{
              marginTop: 28,
              display: "inline-flex",
              background: "var(--white)",
              border: "1px solid var(--border-default)",
              borderRadius: 16,
              padding: "20px 24px",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <img
              alt="Technician Seal of Safety — Drug Tested, Background Checked, Professionally Trained"
              src={sealImg}
              style={{
                display: "block",
                width: "100%",
                maxWidth: 320,
                height: "auto",
              }}
            />
          </div>
        </div>
        <div data-stagger="" style={{ display: "grid", gap: 14 }}>
          {SEAL.map((s) => (
            <div
              key={s.label}
              style={{
                display: "flex",
                gap: 18,
                alignItems: "center",
                background: "var(--cream-100)",
                border: "1px solid var(--border-default)",
                borderRadius: 16,
                padding: "22px 24px",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "var(--red-500)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <CpIcon color="var(--cream-100)" name={s.icon} size={22} />
              </div>
              <span
                style={{
                  fontSize: 19,
                  fontWeight: 800,
                  letterSpacing: "-.01em",
                }}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Licensing Agreements */}
    <section
      id="agreements"
      style={{ background: "var(--ink-500)", padding: "var(--section-y) 0" }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
        }}
      >
        <CpSectionHeading
          align="center"
          eyebrow="Licensing Agreements"
          lead="Review the marks available to your trade, then start the licensing process."
          title="Download the Agreements for Your Trade"
          tone="dark"
        />
        <div
          data-stagger=""
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
            gap: 20,
            marginTop: 48,
          }}
        >
          {TRADES.map((tr) => {
            const files = USP_LICENSING_FILE_URLS[tr.name];
            return (
              <div
                key={tr.name}
                style={{
                  background: "var(--white)",
                  borderRadius: 16,
                  padding: "28px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "var(--red-50)",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CpIcon color="var(--red-500)" name={tr.icon} size={22} />
                  </div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 22,
                      letterSpacing: "-.015em",
                    }}
                  >
                    {tr.name}
                  </h3>
                </div>
                {(files.branding || files.usp) && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <AgreementLink
                      href={files.branding}
                      label="Name Branding Agreement"
                      meta="2025 · PDF"
                    />
                    <AgreementLink
                      href={files.usp}
                      label="USP SFPG"
                      meta="PDF"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section
      id="faq"
      style={{
        background: "var(--sand-200)",
        padding: "var(--section-y) 0 48px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px" }}>
        <CpSectionHeading
          align="center"
          eyebrow="Questions"
          title="Frequently Asked Questions"
        />
        <div style={{ marginTop: 40 }}>
          <CpAccordion items={FAQS} />
        </div>
      </div>
    </section>

    {/* CTA */}
    <section
      style={{
        background: "var(--sand-200)",
        padding: "48px 0 var(--section-y)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{
            background: "var(--gradient-brand)",
            borderRadius: 24,
            padding: "clamp(56px, 7vw, 88px) clamp(24px, 5vw, 56px)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 18,
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--cream-100)",
              fontSize: "var(--text-display-2)",
              lineHeight: 1.08,
              letterSpacing: "-.025em",
              maxWidth: "20ch",
              textWrap: "balance",
            }}
          >
            Stand Out and Build Trust for Only $1 a Year.
          </h2>
          <p
            style={{
              margin: "0 0 14px",
              color: "var(--cream-100)",
              fontSize: 18,
              lineHeight: 1.55,
              maxWidth: "54ch",
            }}
          >
            Download the licensing agreement for your trade to see the marks
            available to you.
          </p>
          <CpButton href="#agreements" size="lg" variant="inverse">
            Get Your Trade&apos;s Agreements
          </CpButton>
        </div>
      </div>
    </section>
  </CpLandingShell>
);

export default UspsTrademarksPage;
